import React, { useCallback, useEffect, useMemo, useState } from 'react'
import clsx from 'clsx'
import AdvertiserTemplateSelector from 'modules/Forms/Account/Create/subcomponents/AdvertiserTemplateSelector'
import AdvertiserFieldsExtended from 'modules/Forms/Account/Create/subcomponents/advertiserFieldsExtended'
import { useTranslation } from 'react-i18next'
import { Grid, Typography } from 'gu-ui-kit'
import Switch from 'components/Switch'
import {
  autofill,
  change,
  formValueSelector,
  getFormAsyncErrors,
  stopAsyncValidation,
} from 'redux-form'
import { useDispatch, useSelector } from 'react-redux'
import PropTypes from 'prop-types'
import initialAdvertiserInfo from 'modules/Forms/Account/Create/initialAdvertiserInfo'
import Tabs from 'components/FormControls/Tabs'
import { DROP_LIST, GET_LIST } from 'modules/Advertisers/List/types'
import omit from 'lodash/omit'
import { useLocalStorage } from 'utils/hooks/useLocalStorage'

const AdvertiserInfoGroup = ({
  builtInto = false,
  form: formName,
  networkType,
  hideable = false,
  autoSave = true,
  defaultFillingType = 'auto',
  submitFailed = false,
  actionButtons = null,
  className = '',
}) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const selector = formValueSelector(formName)

  const {
    advertisers: { data: advertisers = [] } = {},
    form: { advertiserGroupShown = true, advertiser_info } = {},
    formAsyncErrors,
  } = useSelector((state) => ({
    form: selector(state, 'advertiserGroupShown', 'advertiser_info'),
    formAsyncErrors: getFormAsyncErrors(formName)(state),
    advertisers: state['advertisers-list'],
  }))

  const [advertiserPresetConfig, setAdvertiserPresetConfig] = useLocalStorage(
    'CreateAccountAdvertiserPresetConfig',
    {},
  )
  const [fillingTypeConfig, setFillingTypeConfig] = useLocalStorage(
    'CreateAccountFillingTypeConfig',
    { [formName]: defaultFillingType },
  )

  const [selectedId, setSelectedId] = useState(null)

  const isAdvertiserFieldsHasErrors = useMemo(
    () => formAsyncErrors?.advertiser_info ?? false,
    [formAsyncErrors],
  )

  useEffect(() => {
    dispatch({
      type: GET_LIST,
      payload: {
        params: {
          offset: 0,
          limit: 1000,
          page: 1,
          status: 'active',
        },
      },
    })

    return () => {
      dispatch({ type: DROP_LIST })
    }
  }, [])

  const clearSelectedAdvertiser = useCallback(() => {
    dispatch(autofill(formName, 'advertiser_info', { ...initialAdvertiserInfo }))
    dispatch(stopAsyncValidation(formName))
    setSelectedId(null)
    setAdvertiserPresetConfig({
      [formName]: null,
    })
  }, [formName])

  const handleChange = useCallback(
    (selectedId) => {
      if (!selectedId) {
        clearSelectedAdvertiser()
        return
      } else {
        const selectedAdvertiserInfo = advertisers.find(({ id }) => id === +selectedId)
        setAdvertiserPresetConfig({
          [formName]: selectedAdvertiserInfo?.id ?? null,
        })
        dispatch(
          change(
            formName,
            'advertiser_info',
            {
              ...omit(selectedAdvertiserInfo || initialAdvertiserInfo, ['id']),
            },
            true,
            true,
          ),
        )
      }
      setSelectedId(selectedId)
    },
    [advertisers],
  )

  const handleChangeFillingType = useCallback(
    (value) => {
      // if (value === 'manually') {
      clearSelectedAdvertiser()
      // }
      setFillingTypeConfig({ [formName]: value })
    },
    [formName],
  )

  useEffect(() => {
    if (autoSave) {
      setSelectedId(advertiserPresetConfig?.[formName] ?? null)
    }
  }, [])

  useEffect(() => {
    if (!fillingTypeConfig[formName] || fillingTypeConfig[formName] === null) {
      setFillingTypeConfig({ [formName]: defaultFillingType })
    }
  }, [fillingTypeConfig, formName, defaultFillingType])

  return (
    <div className={className}>
      <Grid
        justifyContent={'between'}
        alignItems={'start'}
        className={clsx({
          'margin-bottom-24': !hideable || advertiserGroupShown,
        })}
        wrap={false}
      >
        <Grid
          direction={'column'}
          gap={8}
        >
          <Typography
            variant={'Subtitle-30'}
            component={'h3'}
            color={'Label-Primary'}
          >
            {t('services::forms.shared_fields.advertiser.show_advertisers.title')}
          </Typography>

          <Typography
            variant={'Body-15'}
            component={'p'}
            noMargin
            color={'Label-Secondary'}
          >
            {t('services::forms.shared_fields.advertiser.show_advertisers.summary')}
          </Typography>
        </Grid>

        {hideable && (
          <Switch
            checked={advertiserGroupShown}
            onChange={() =>
              dispatch(
                change(formName, 'advertiserGroupShown', !advertiserGroupShown, false, false),
              )
            }
          />
        )}
      </Grid>

      {((hideable && advertiserGroupShown) || !hideable) && (
        <div className={'margin-bottom-24'}>
          <Tabs
            value={fillingTypeConfig[formName]}
            onChange={handleChangeFillingType}
            options={[
              { id: 'auto', label: t('services::forms.register_account.select_from_list') },
              { id: 'manually', label: t('services::forms.register_account.fill_manually') },
            ]}
          />
        </div>
      )}

      {!!advertiserGroupShown && (
        <>
          {fillingTypeConfig[formName] === 'auto' && (
            <AdvertiserTemplateSelector
              selectedId={selectedId}
              onChange={handleChange}
            />
          )}

          <div
            style={{
              display:
                selectedId ||
                fillingTypeConfig[formName] === 'manually' ||
                (isAdvertiserFieldsHasErrors && submitFailed)
                  ? 'block'
                  : 'none',
            }}
          >
            <AdvertiserFieldsExtended
              builtInto={builtInto}
              form={formName}
              advertiser_info={advertiser_info}
              networkType={networkType}
            />
          </div>
        </>
      )}
      {actionButtons}
    </div>
  )
}

AdvertiserInfoGroup.propTypes = {
  builtInto: PropTypes.bool,
  className: PropTypes.string,
  form: PropTypes.string,
  hideable: PropTypes.bool,
  advertiser_info: PropTypes.object,
  networkType: PropTypes.string,
  autoSave: PropTypes.bool,
  defaultFillingType: PropTypes.oneOf(['auto', 'manually']),
  submitFailed: PropTypes.bool,
  actionButtons: PropTypes.node,
}

export default AdvertiserInfoGroup
